import React from "react";
import { Link } from "react-router-dom";
import { Loader2, FlaskConical, Inbox } from "lucide-react";

export const Eyebrow = ({ children }: { children: React.ReactNode }) => <p className="eyebrow">{children}</p>;

export const PageHeader = ({ eyebrow, title, intro, testid }: { eyebrow: string; title: string; intro?: string; testid?: string }) => (
  <section className="border-b border-navy/10 bg-white/60">
    <div className="container-x py-14 sm:py-20 fade-up">
      <Eyebrow>{eyebrow}</Eyebrow>
      <h1 data-testid={testid || "page-title"} className="mt-4 max-w-3xl text-4xl leading-[1.08] tracking-tight sm:text-5xl lg:text-6xl">{title}</h1>
      {intro && <p className="mt-5 max-w-2xl text-base text-navy/65 md:text-lg">{intro}</p>}
    </div>
  </section>
);

const STATUS: Record<string, string> = {
  pending: "bg-amber-50 text-amber-800 border-amber-200",
  approved: "bg-blue-50 text-blue-800 border-blue-200",
  confirmed: "bg-emerald-50 text-emerald-800 border-emerald-200",
  declined: "bg-red-50 text-red-800 border-red-200",
  cancelled: "bg-slate-100 text-slate-600 border-slate-200",
  completed: "bg-violet-50 text-violet-800 border-violet-200",
  open: "bg-amber-50 text-amber-800 border-amber-200",
  in_progress: "bg-blue-50 text-blue-800 border-blue-200",
  resolved: "bg-emerald-50 text-emerald-800 border-emerald-200",
  closed: "bg-slate-100 text-slate-600 border-slate-200",
  new: "bg-amber-50 text-amber-800 border-amber-200",
  contacted: "bg-blue-50 text-blue-800 border-blue-200",
  sent: "bg-emerald-50 text-emerald-800 border-emerald-200",
  failed: "bg-red-50 text-red-800 border-red-200",
  not_configured: "bg-slate-100 text-slate-600 border-slate-200",
  paid: "bg-emerald-50 text-emerald-800 border-emerald-200",
  deposit_paid: "bg-teal-50 text-teal-800 border-teal-200",
  unpaid: "bg-slate-100 text-slate-600 border-slate-200",
  refunded: "bg-violet-50 text-violet-800 border-violet-200",
  rejected: "bg-red-50 text-red-800 border-red-200",
};

export const StatusBadge = ({ status, testid }: { status: string; testid?: string }) => (
  <span data-testid={testid} className={`inline-flex items-center rounded-full border px-2.5 py-0.5 font-mono text-[10px] font-semibold uppercase tracking-wider ${STATUS[status] || STATUS.unpaid}`}>
    {status?.replace(/_/g, " ")}
  </span>
);

export const SandboxBadge = ({ className = "" }: { className?: string }) => (
  <span data-testid="sandbox-badge" className={`inline-flex items-center gap-1.5 rounded-full border border-amber-400 bg-amber-50 px-3 py-1 font-mono text-[10px] font-bold uppercase tracking-widest text-amber-700 ${className}`}>
    <FlaskConical size={12} /> Non-live · Test mode
  </span>
);

export const Spinner = ({ label = "Loading" }: { label?: string }) => (
  <div className="flex items-center justify-center gap-2 py-16 text-sm text-navy/50" data-testid="loading-indicator">
    <Loader2 className="animate-spin" size={18} /> {label}
  </div>
);

export const EmptyState = ({ title, body, cta, to, testid }: { title: string; body: string; cta?: string; to?: string; testid?: string }) => (
  <div data-testid={testid || "empty-state"} className="card-lux flex flex-col items-start gap-4 p-8 sm:p-12">
    <span className="flex h-11 w-11 items-center justify-center rounded-full bg-gold-soft text-navy"><Inbox size={20} /></span>
    <h3 className="text-2xl">{title}</h3>
    <p className="max-w-xl text-sm text-navy/65 md:text-base">{body}</p>
    {cta && to && <Link to={to} className="btn-navy" data-testid={`${testid || "empty-state"}-cta`}>{cta}</Link>}
  </div>
);

export const Consent = ({ checked, onChange, text, testid }: { checked: boolean; onChange: (v: boolean) => void; text: React.ReactNode; testid: string }) => (
  <label className="flex cursor-pointer items-start gap-3 text-sm text-navy/70">
    <input type="checkbox" data-testid={testid} checked={checked} onChange={(e) => onChange(e.target.checked)} className="mt-0.5 h-4 w-4 accent-[#1B6B46]" />
    <span>{text}</span>
  </label>
);
